import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoChevronForward, IoTrashOutline, IoMailOutline } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { createProspect, getProspectsByRealtor, deleteProspect, markProspectMessaged } from '../../../services/prospectService';
import { requestShare } from '../../../bridge/webBridge';

const formatPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const ProspectRegister = () => {
  const { userData } = useAuth();
  const uid = userData?.uid;
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [prospects, setProspects] = useState([]);

  const formUrl = `https://ieum-landing.web.app/apply?ref=${uid || 'demo'}`;

  const loadProspects = async () => {
    if (!uid) return;
    try {
      const list = await getProspectsByRealtor(uid);
      setProspects(list);
    } catch (e) {
      console.error('[ProspectRegister] loadProspects failed:', e);
    }
  };

  useEffect(() => {
    loadProspects();
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!name.trim()) { alert('이름을 입력해주세요.'); return; }
    const rawPhone = phone.replace(/\D/g, '');
    if (rawPhone.length < 10) { alert('전화번호를 확인해주세요.'); return; }
    if (!uid) { alert('로그인이 필요합니다.'); return; }
    setSubmitting(true);
    try {
      await createProspect(uid, { name: name.trim(), phone: rawPhone, memo: memo.trim() });
      setName(''); setPhone(''); setMemo('');
      alert('가망고객이 등록되었습니다.');
      loadProspects();
    } catch (err) {
      alert(err.message || '등록 실패');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try { await deleteProspect(id); loadProspects(); } catch { /* skip */ }
  };

  const handleSms = (p) => {
    const msg = `안녕하세요.\n이사를 준비하시면서 필요한 서비스를 한번에 신청하세요.\n\n[무료 견적 신청하기]\n${formUrl}`;
    if (p?.id) {
      markProspectMessaged(p.id, 'sms').catch(() => {}).finally(loadProspects);
    }
    window.location.href = `sms:${p?.phone || ''}?body=${encodeURIComponent(msg)}`;
  };

  const handleKakao = () => {
    const shared = requestShare({
      title: '이음 - 무료 견적 신청',
      text: '이사를 준비하시면서 필요한 서비스를 한번에 신청하세요.',
      url: formUrl,
    });
    if (!shared) {
      navigator.clipboard?.writeText(formUrl).then(() => {
        alert('링크가 클립보드에 복사되었습니다.\n카카오톡에 붙여넣기 해주세요.');
      });
    }
  };

  return (
    <Wrap>
      {/* 상단 2 카드 */}
      <TopCardRow>
        <TopCard>
          <TopCardLabel>등록</TopCardLabel>
          <TopCardValue>{prospects.length}</TopCardValue>
        </TopCard>
        <TopCard>
          <TopCardLabel>메시지 발송</TopCardLabel>
          <TopCardValue>{prospects.filter((p) => p.last_sent_at).length}</TopCardValue>
        </TopCard>
      </TopCardRow>

      {/* 신규 등록 섹션 */}
      <SectionTitle>신규 등록</SectionTitle>
      <LinkList>
        <LinkRow onClick={() => setShowForm(!showForm)}>
          <LinkText>신규 가망고객 DB 등록 &gt;&gt;&gt;</LinkText>
          <IoChevronForward size={16} color="#9CA3AF" />
        </LinkRow>
        <LinkRow onClick={() => alert('대량 등록 기능은 준비 중입니다.')}>
          <LinkText>신규 가망고객 DB 대량 등록 &gt;&gt;&gt;</LinkText>
          <IoChevronForward size={16} color="#9CA3AF" />
        </LinkRow>
      </LinkList>

      {/* 신규 등록 폼 (토글) */}
      {showForm && (
        <FormCard>
          <FormGroup>
            <Label>이름 *</Label>
            <Input placeholder="홍길동" value={name} onChange={(e) => setName(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>전화번호 *</Label>
            <Input placeholder="010-1234-5678" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} inputMode="tel" />
          </FormGroup>
          <FormGroup>
            <Label>메모</Label>
            <Textarea placeholder="메모 (선택)" value={memo} onChange={(e) => setMemo(e.target.value)} />
          </FormGroup>
          <SubmitBtn disabled={submitting} onClick={handleSubmit}>
            {submitting ? '등록 중...' : '가망고객 등록'}
          </SubmitBtn>
        </FormCard>
      )}

      {/* 메시지 발송 섹션 */}
      <SectionTitle>메시지 발송</SectionTitle>
      <LinkList>
        <LinkRow onClick={() => handleSms(null)}>
          <LinkText>SMS/LMS 메시지 보내기 &gt;&gt;&gt;</LinkText>
          <IoChevronForward size={16} color="#9CA3AF" />
        </LinkRow>
        <LinkRow onClick={handleKakao}>
          <LinkText>Kakao 메시지 보내기 &gt;&gt;&gt;</LinkText>
          <IoChevronForward size={16} color="#9CA3AF" />
        </LinkRow>
      </LinkList>

      {/* 등록된 가망고객 목록 */}
      {prospects.length > 0 && (
        <>
          <SectionTitle>등록된 가망고객 ({prospects.length})</SectionTitle>
          <ProspectList>
            {prospects.map((p) => (
              <ProspectItem key={p.id}>
                <ProspectInfo>
                  <ProspectName>{p.name}</ProspectName>
                  <ProspectPhone>{formatPhone(p.phone)}</ProspectPhone>
                  {p.memo && <ProspectMemo>{p.memo}</ProspectMemo>}
                </ProspectInfo>
                <IconBtnRow>
                  <IconBtn onClick={() => handleSms(p)} title="문자 발송"><IoMailOutline size={16} /></IconBtn>
                  <IconBtn onClick={() => handleDelete(p.id)} title="삭제"><IoTrashOutline size={16} /></IconBtn>
                </IconBtnRow>
              </ProspectItem>
            ))}
          </ProspectList>
        </>
      )}
    </Wrap>
  );
};

export default ProspectRegister;

// ───── 스타일 ─────
const Wrap = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TopCardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const TopCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: 12px;
  padding: 18px 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TopCardLabel = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const TopCardValue = styled.p`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.primary};
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm};
`;

const LinkList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 12px;
  overflow: hidden;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  background: #fff;
  &:not(:last-child) { border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight}; }
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const LinkText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  outline: none; box-sizing: border-box; resize: none; min-height: 80px; font-family: inherit;
  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  &:disabled { opacity: 0.6; }
`;

const ProspectList = styled.div``;

const ProspectItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  margin-bottom: 8px;
`;

const ProspectInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProspectName = styled.p`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ProspectPhone = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProspectMemo = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

const IconBtnRow = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: #fff;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;
